import { useState, useEffect, useCallback } from 'react';
import { getAllEvents, getUpcomingEvents, getPastEvents, getEventById } from '../lib/supabase';

export const useEvents = (options = {}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let result;
      
      console.log('Fetching events, type:', options.type);
      
      if (options.type === 'upcoming') {
        result = await getUpcomingEvents(options.limit);
      } else if (options.type === 'past') {
        result = await getPastEvents(options.limit);
      } else {
        result = await getAllEvents();
      }
      
      console.log('Events result:', result);
      
      if (result.error) {
        console.error('Events fetch error:', result.error);
        throw result.error;
      }
      
      // Transform data to match existing app structure
      const transformedEvents = (result.data || []).map(event => ({
        id: event.id,
        title: event.title,
        description: event.description,
        org: event.organization?.name || '',
        organization: event.organization?.name || '',
        event: event.title,
        status: event.status,
        city: event.location || [event.venue?.city, event.venue?.state, event.venue?.country].filter(Boolean).join(', '),
        location: event.location || '',
        venue: event.venue_name || event.venue?.name || '',
        venue_name: event.venue_name || event.venue?.name || '',
        address: event.venue?.address || event.location || '',
        country: event.venue?.country || '',
        airport: event.airport || event.nearest_airport || '',
        latitude: event.venue?.latitude || event.latitude || null,
        longitude: event.venue?.longitude || event.longitude || null,
        date: event.start_date,
        start: event.start_date,
        end: event.end_date,
        featured: event.is_featured,
        website_url: event.website_url,
        registration_url: event.registration_url,
        image_url: event.image_url,
        event_type: event.event_type || 'in_person',
        virtual_platform: event.virtual_platform,
        virtual_url: event.virtual_url,
        virtual_meeting_id: event.virtual_meeting_id,
        virtual_passcode: event.virtual_passcode,
        event_audience: event.event_audience || 'general',
        event_category: event.event_category || 'conference',
        is_paid_event: event.is_paid_event || false,
        registration_fee: event.registration_fee || 0,
        hotels: event.hotels || null,
        notes: event.notes || null,
        // Keep original data too
        _raw: event
      }));
      
      setEvents(transformedEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, [options.type, options.limit]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return { events, loading, error, refetch: fetchEvents };
};

export const useEvent = (eventId) => {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const { data, error: fetchError } = await getEventById(eventId);
        
        if (fetchError) {
          throw fetchError;
        }
        
        if (data) {
          // Transform to match existing app structure
          const transformedEvent = {
            id: data.id,
            title: data.title,
            description: data.description,
            org: data.organization?.name || '',
            organization: data.organization?.name || '',
            event: data.title,
            status: data.status,
            city: data.location || [data.venue?.city, data.venue?.state, data.venue?.country].filter(Boolean).join(', '),
            location: data.location || '',
            venue: data.venue_name || data.venue?.name || '',
            venue_name: data.venue_name || data.venue?.name || '',
            address: data.venue?.address || data.location || '',
            country: data.venue?.country || '',
            airport: data.airport || data.nearest_airport || '',
            latitude: data.venue?.latitude || data.latitude || null,
            longitude: data.venue?.longitude || data.longitude || null,
            date: data.start_date,
            start: data.start_date,
            end: data.end_date,
            featured: data.is_featured,
            website_url: data.website_url,
            registration_url: data.registration_url,
            image_url: data.image_url,
            event_type: data.event_type || 'in_person',
            virtual_platform: data.virtual_platform,
            virtual_url: data.virtual_url,
            virtual_meeting_id: data.virtual_meeting_id,
            virtual_passcode: data.virtual_passcode,
            event_audience: data.event_audience || 'general',
            event_category: data.event_category || 'conference',
            is_paid_event: data.is_paid_event || false,
            registration_fee: data.registration_fee || 0,
            hotels: data.hotels || null,
            notes: data.notes || null,
            speakers: data.event_speakers?.map(es => ({
              ...es.speaker,
              session_title: es.session_title,
              session_date: es.session_date,
              session_time: es.session_time,
              topic: es.topic
            })) || [],
            _raw: data
          };
          
          setEvent(transformedEvent);
        }
      } catch (err) {
        console.error('Error fetching event:', err);
        setError(err.message || 'Failed to fetch event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  return { event, loading, error };
};

export default useEvents;
